/**
 * Workspace layout preset types.
 *
 * Owners and admins can publish named layout presets for their workspace.
 * Members can adopt a preset as their personal starting configuration.
 * Adopting a preset copies the values into the user's local layoutStore -
 * it does not lock the user into that layout.
 */

export interface LayoutPresetSidebar {
  position: 'left' | 'right'
  width: number
  collapsed: boolean
}

export interface LayoutPresetWidget {
  widgetId: string
  visible: boolean
  order: number
  col: number
  row: number
  colSpan: number
  rowSpan: number
  config?: { token?: string; name?: string }
}

export interface LayoutPresetSnapshot {
  mode: 'classic' | 'desktop'
  sidebar: LayoutPresetSidebar
  splitView: {
    enabled: boolean
    split: number
  }
  /** @deprecated Use dashboards instead. Kept for backward compat with old presets. */
  dashboard?: {
    widgets: LayoutPresetWidget[]
  }
  /** Both classic and desktop widget layouts. Preferred over `dashboard`. */
  dashboards?: {
    classic: { widgets: LayoutPresetWidget[] }
    desktop: { widgets: LayoutPresetWidget[] }
  }
  /** Nav item order and visibility at publish time. */
  nav?: {
    order: string[]
    hidden: string[]
  }
  /** Widget layouts for extra dashboard pages at snapshot time, keyed by page ID. */
  extraPageWidgets?: Record<string, LayoutPresetWidget[]>
  /**
   * Full page data (slug + name + widgets) for cross-account preset application.
   * When applying a preset, pages are matched by slug; missing pages are created.
   * pageId is the original page's UUID, used to remap dp_<id> nav order entries.
   * Preferred over extraPageWidgets for new presets.
   */
  presetPages?: Array<{
    pageId: string
    slug: string
    name: string
    description?: string
    widgets: LayoutPresetWidget[]
  }>
}

export interface WorkspaceLayoutPreset {
  id: string
  name: string
  description?: string
  createdBy: string   // userId
  createdAt: string
  layout: LayoutPresetSnapshot
  /** When true, new workspace members automatically start with this layout. Only one preset can be default. */
  isDefault?: boolean
}

/** Shape of the layoutPresets JSON column on Workspace */
export type WorkspaceLayoutPresetsData = WorkspaceLayoutPreset[]


// =============================================================================
// User Dashboard Pages
// =============================================================================

/**
 * An additional named dashboard page created by a user.
 * Lives at /d/:slug in the app.
 * Widget layouts are stored here; metadata is persisted to User.preferences.
 */
export interface DashboardPage {
  id: string
  slug: string              // URL-safe, e.g. "sales-overview" → /d/sales-overview
  name: string              // Display name in nav and page header
  description?: string      // Optional subtitle shown in manager
  navOrder: number          // Position in the sidebar nav
  navVisible: boolean       // Whether it appears in the nav
  widgets: LayoutPresetWidget[]
  createdAt: string
  updatedAt: string
  /** Phase 7: true when the user has downgraded below this page's tier limit (read-only until upgrade). */
  contained?: boolean
}

/**
 * A named snapshot of a single page or all dashboard pages.
 * Users can save and restore these to roll back to a known layout.
 */
export interface SavedLayoutSnapshot {
  id: string
  name: string
  createdAt: string
  /** 'single_page' captures one page's widget layout; 'all_pages' captures all pages + nav order */
  scope: 'single_page' | 'all_pages'
  /** The page that was captured. null = core dashboard. Only set when scope = 'single_page'. */
  dashboardPageId: string | null
  widgets: LayoutPresetWidget[]         // captured widget layout
  allPages?: DashboardPage[]            // only when scope = 'all_pages'
  /** Global nav item order captured at save time. Only set when scope = 'all_pages'. */
  navOrder?: string[]
  /** Global nav item hidden IDs captured at save time. Only set when scope = 'all_pages'. */
  navHidden?: string[]
}

/**
 * A header shortcut button pinned to a specific page.
 * Stored per route name so the correct shortcuts surface in each view's header.
 */
export interface HeaderShortcutData {
  id: string
  type: 'form_embed' | 'sheet_embed' | 'chart_embed'
  token: string
  name: string
}

/**
 * The layout fields that constitute a full workspace-scoped live state.
 * Used for both activeState and backupState.
 */
export interface WorkspaceLayoutState {
  /** Active layout mode - must be persisted to backend so it survives localStorage clears. */
  mode?: 'classic' | 'desktop'
  classicDashboard?: { widgets: LayoutPresetWidget[]; updatedAt: string }
  desktopDashboard?: { widgets: LayoutPresetWidget[]; updatedAt: string }
  navOrder?: string[]
  navHidden?: string[]
  headerShortcuts?: Record<string, HeaderShortcutData[]>
  /**
   * Full snapshot of all extra dashboard pages (id, slug, name, navOrder,
   * navVisible, widgets). Used for cross-device sync and backup/restore.
   * Not used in LayoutPresetSnapshot - presets use extraPageWidgets instead.
   */
  extraPages?: DashboardPage[]
}

/**
 * Per-workspace section of User.preferences.
 * Keyed by workspaceId inside the top-level JSON blob.
 */
export interface WorkspaceUserPreferences {
  /**
   * The user's current live layout for this workspace.
   * Auto-synced by the debounced backend sync after any layout mutation.
   */
  activeState?: WorkspaceLayoutState
  /**
   * Snapshot of the layout before the user first applied a preset in their
   * current browsing session. Set on the first Apply, never overwritten by
   * subsequent Applies (so the user can always get back to where they started).
   * Cleared when the user restores it or explicitly discards it.
   */
  backupState?: WorkspaceLayoutState
  /** @deprecated - flat fields migrated into activeState on first read */
  coreDashboard?: { widgets: LayoutPresetWidget[]; updatedAt: string }
  classicDashboard?: { widgets: LayoutPresetWidget[]; updatedAt: string }
  desktopDashboard?: { widgets: LayoutPresetWidget[]; updatedAt: string }
  navOrder?: string[]
  navHidden?: string[]
  headerShortcuts?: Record<string, HeaderShortcutData[]>
  /** Additional user-created dashboard pages (unchanged) */
  dashboardPages: DashboardPage[]
  savedLayoutSnapshots: SavedLayoutSnapshot[]
}

/**
 * Shape of the preferences JSON column on User.
 * All keys are workspaceIds mapping to per-workspace preferences.
 */
export type UserPreferences = {
  [workspaceId: string]: WorkspaceUserPreferences | undefined
}

/** Reserved slugs that users may not use for dashboard pages */
export const RESERVED_DASHBOARD_SLUGS: readonly string[] = [
  'main', 'new', 'edit', 'api', 'app', 'public', 'admin',
] as const

/** Validate a dashboard page slug. Returns null if valid, error message if invalid. */
export function validateDashboardSlug(slug: string): string | null {
  if (!slug) return 'Slug is required'
  if (slug.length > 60) return 'Slug must be 60 characters or fewer'
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
    return 'Slug may only contain lowercase letters, numbers, and hyphens'
  }
  if (RESERVED_DASHBOARD_SLUGS.includes(slug)) {
    return `"${slug}" is reserved and cannot be used`
  }
  return null
}
